import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Schema.org JSON-LD
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TravelAgency",
        "@id": `${siteUrl}/#organization`,
        "name": "FF Travel Angola",
        "legalName": "FF Prestação & Serviço, Lda",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/821bfe14b1fc609bf2a57dd541a10a42.png"
        },
        "image": "https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/821bfe14b1fc609bf2a57dd541a10a42.png",
        "description": "Agência de viagens em Luanda, Angola. Passagens aéreas, vistos, pacotes turísticos internacionais.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Rua do Campo do Escoqueiro, de frente ao prédio do Cucu",
          "addressLocality": "Luanda",
          "addressCountry": "AO"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "-8.8383",
          "longitude": "13.2344"
        },
        "telephone": ["+244936769113", "+244927606910"],
        "priceRange": "$$",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "17:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "08:00",
            "closes": "12:00"
          }
        ],
        "sameAs": [
          "https://www.instagram.com/ff_travel_angola/",
          "https://www.facebook.com/share/1AVqwyfH7E/",
          "https://www.linkedin.com/company/ff-travel"
        ]
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "FF Travel Angola",
        "publisher": {
          "@id": `${siteUrl}/#organization`
        }
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/#webpage`,
        "url": siteUrl,
        "name": "FF Travel Luanda - Agência de Viagens Angola",
        "isPartOf": {
          "@id": `${siteUrl}/#website`
        },
        "about": {
          "@id": `${siteUrl}/#organization`
        },
        "description": "Agência de viagens em Luanda. Passagens aéreas, vistos rápidos, pacotes turísticos para Brasil, Portugal, EUA, Canadá e mais."
      }
    ]
  };

  // Injetar Schema.org no head
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = 'schema-home';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('schema-home');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  // Slides do Hero com destinos específicos solicitados
  const heroSlides = [
    {
      image: 'https://public.readdy.ai/ai/img_res/b9f652947d25b81c84f24270039da79c.jpg',
      destination: 'Brasil',
      flag: '🇧🇷',
      tagline: 'Viaje com a FF Travel',
      highlight: 'Realize seu sonho brasileiro',
      alt: 'Avião FF Travel sobrevoando Cristo Redentor no Rio de Janeiro Brasil ao pôr do sol - viagens internacionais para o Brasil'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=FF%20Travel%20airplane%20soaring%20above%20Lisbon%20Portugal%20colorful%20cityscape%20with%20yellow%20tram%20and%20Tagus%20river%2C%20charming%20European%20travel%20advertisement%2C%20Portuguese%20landmarks%20with%20aircraft%2C%20professional%20tourism%20marketing%20photography%2C%20sunny%20Mediterranean%20atmosphere%2C%20vibrant%20city%20view%2C%20wanderlust%20inspiration%2C%20airline%20branding&width=800&height=450&seq=hero-portugal-fftravel-003&orientation=landscape',
      destination: 'Portugal',
      flag: '🇵🇹',
      tagline: 'Viaje com a FF Travel',
      highlight: 'Realize seu sonho português',
      alt: 'Avião FF Travel sobre Lisboa Portugal com elétrico amarelo e rio Tejo - pacotes turísticos para Portugal'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=FF%20Travel%20airplane%20flying%20over%20New%20York%20Manhattan%20skyline%20with%20Empire%20State%20Building%20at%20sunset%2C%20iconic%20American%20travel%20advertisement%2C%20NYC%20landmarks%20with%20aircraft%2C%20professional%20tourism%20marketing%20photography%2C%20vibrant%20urban%20destination%2C%20golden%20hour%20cityscape%2C%20wanderlust%20inspiration%2C%20airline%20branding&width=800&height=450&seq=hero-usa-fftravel-003&orientation=landscape',
      destination: 'Estados Unidos',
      flag: '🇺🇸',
      tagline: 'Viaje com a FF Travel',
      highlight: 'Realize seu sonho americano',
      alt: 'Avião FF Travel sobre Nova York Manhattan com Empire State Building - viagens para Estados Unidos'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=FF%20Travel%20airplane%20above%20Toronto%20Canada%20CN%20Tower%20and%20beautiful%20lake%20Ontario%20at%20sunset%2C%20stunning%20Canadian%20travel%20advertisement%2C%20iconic%20landmarks%20with%20aircraft%2C%20professional%20tourism%20marketing%20photography%2C%20modern%20city%20destination%2C%20golden%20evening%20light%2C%20wanderlust%20inspiration%2C%20airline%20branding&width=800&height=450&seq=hero-canada-fftravel-002&orientation=landscape',
      destination: 'Canadá',
      flag: '🇨🇦',
      tagline: 'Viaje com a FF Travel',
      highlight: 'Realize seu sonho canadense',
      alt: 'Avião FF Travel sobre Toronto Canadá com CN Tower e Lago Ontário - pacotes de viagem para o Canadá'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=FF%20Travel%20airplane%20soaring%20over%20London%20England%20Big%20Ben%20and%20Thames%20river%20at%20golden%20hour%2C%20classic%20British%20travel%20advertisement%2C%20iconic%20UK%20landmarks%20with%20aircraft%2C%20professional%20tourism%20marketing%20photography%2C%20historic%20European%20destination%2C%20warm%20sunset%20lighting%2C%20wanderlust%20inspiration%2C%20airline%20branding&width=800&height=450&seq=hero-uk-fftravel-003&orientation=landscape',
      destination: 'Inglaterra',
      flag: '🇬🇧',
      tagline: 'Viaje com a FF Travel',
      highlight: 'Realize seu sonho inglês',
      alt: 'Avião FF Travel sobre Londres Inglaterra com Big Ben e rio Tâmisa - viagens para Reino Unido'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=FF%20Travel%20airplane%20flying%20over%20Cape%20Town%20South%20Africa%20Table%20Mountain%20and%20beautiful%20coastline%20at%20sunset%2C%20stunning%20African%20travel%20advertisement%2C%20iconic%20landmarks%20with%20aircraft%2C%20professional%20tourism%20marketing%20photography%2C%20breathtaking%20destination%2C%20golden%20hour%20lighting%2C%20wanderlust%20inspiration%2C%20airline%20branding&width=800&height=450&seq=hero-southafrica-fftravel-002&orientation=landscape',
      destination: 'África do Sul',
      flag: '🇿🇦',
      tagline: 'Viaje com a FF Travel',
      highlight: 'Realize seu sonho sul-africano',
      alt: 'Avião FF Travel sobre Cidade do Cabo África do Sul com Table Mountain - destinos turísticos na África'
    }
  ];

  // Auto-play do carrossel hero
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToHeroSlide = (index) => {
    setCurrentHeroSlide(index);
  };

  const nextHeroSlide = () => {
    setCurrentHeroSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevHeroSlide = () => {
    setCurrentHeroSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Scroll reveal animation
      const reveals = document.querySelectorAll('.scroll-reveal');
      reveals.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('revealed');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloquear scroll quando galeria está aberta
  useEffect(() => {
    if (galleryOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [galleryOpen]);

  // Navegação com teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryOpen) return;
      
      if (e.key === 'Escape') {
        setGalleryOpen(false);
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, currentImageIndex]);

  const destinations = [
    { 
      name: 'Dubai', 
      flag: '🇦🇪', 
      image: 'https://readdy.ai/api/search-image?query=Dubai%20skyline%20at%20golden%20sunset%20with%20Burj%20Khalifa%20towering%20over%20modern%20cityscape%2C%20luxury%20skyscrapers%20reflecting%20in%20water%2C%20futuristic%20architecture%2C%20warm%20orange%20and%20pink%20sky%2C%20professional%20travel%20photography%2C%20iconic%20Middle%20East%20destination%2C%20breathtaking%20urban%20landscape%2C%20pristine%20clear%20view&width=500&height=333&seq=dubai-dest-full-002&orientation=landscape',
      description: 'Descubra o luxo e a modernidade de Dubai',
      highlight: 'Burj Khalifa, compras de luxo e deserto',
      alt: 'Skyline de Dubai ao pôr do sol com Burj Khalifa - pacotes turísticos para Dubai Emirados Árabes'
    },
    { 
      name: 'China', 
      flag: '🇨🇳', 
      image: 'https://readdy.ai/api/search-image?query=Great%20Wall%20of%20China%20snaking%20through%20misty%20green%20mountains%20at%20sunrise%2C%20ancient%20stone%20fortification%2C%20dramatic%20landscape%2C%20cultural%20heritage%20wonder%2C%20professional%20travel%20photography%2C%20iconic%20Asian%20landmark%2C%20majestic%20scenery%2C%20golden%20morning%20light%2C%20breathtaking%20view&width=500&height=333&seq=china-dest-full-002&orientation=landscape',
      description: 'Explore a história milenar e cultura fascinante',
      highlight: 'Grande Muralha, Cidade Proibida e gastronomia',
      alt: 'Grande Muralha da China serpenteando por montanhas verdes - viagens culturais para a China'
    },
    { 
      name: 'Portugal', 
      flag: '🇵🇹', 
      image: 'https://readdy.ai/api/search-image?query=Lisbon%20Portugal%20colorful%20pastel%20buildings%20with%20iconic%20yellow%20tram%20on%20cobblestone%20street%2C%20charming%20historic%20European%20city%2C%20traditional%20Portuguese%20architecture%2C%20sunny%20blue%20sky%2C%20professional%20travel%20photography%2C%20vibrant%20atmosphere%2C%20picturesque%20urban%20scene&width=500&height=333&seq=portugal-dest-full-002&orientation=landscape',
      description: 'Visite a terra dos seus antepassados',
      highlight: 'Lisboa, Porto, praias do Algarve e história',
      alt: 'Elétrico amarelo em Lisboa Portugal com edifícios coloridos - turismo em Portugal para angolanos'
    },
    { 
      name: 'Espanha', 
      flag: '🇪🇸', 
      image: 'https://readdy.ai/api/search-image?query=Barcelona%20Sagrada%20Familia%20cathedral%20with%20intricate%20spires%20against%20blue%20sky%2C%20stunning%20Spanish%20architecture%20by%20Gaudi%2C%20iconic%20landmark%2C%20sunny%20Mediterranean%20atmosphere%2C%20professional%20travel%20photography%2C%20cultural%20destination%2C%20magnificent%20detailed%20facade%2C%20vibrant%20European%20cityscape&width=500&height=333&seq=spain-dest-full-002&orientation=landscape',
      description: 'Viva a paixão espanhola e arquitetura única',
      highlight: 'Barcelona, Madrid, praias mediterrânicas',
      alt: 'Sagrada Família em Barcelona Espanha com arquitetura de Gaudí - viagens para Espanha'
    },
    { 
      name: 'França', 
      flag: '🇫🇷', 
      image: 'https://readdy.ai/api/search-image?query=Eiffel%20Tower%20Paris%20France%20at%20romantic%20sunset%20with%20golden%20sky%2C%20iconic%20iron%20structure%2C%20Seine%20river%20view%2C%20elegant%20French%20landmark%2C%20warm%20evening%20light%2C%20professional%20travel%20photography%2C%20dreamy%20European%20destination%2C%20breathtaking%20city%20panorama&width=500&height=333&seq=france-dest-full-002&orientation=landscape',
      description: 'Conheça a cidade do amor e da elegância',
      highlight: 'Paris, Torre Eiffel, Louvre e gastronomia',
      alt: 'Torre Eiffel em Paris França ao pôr do sol romântico - pacotes para Paris cidade do amor'
    },
    { 
      name: 'Bélgica', 
      flag: '🇧🇪', 
      image: 'https://readdy.ai/api/search-image?query=Brussels%20Grand%20Place%20Belgium%20with%20ornate%20golden%20guild%20houses%20at%20twilight%2C%20historic%20European%20square%2C%20baroque%20architecture%2C%20illuminated%20buildings%2C%20professional%20travel%20photography%2C%20charming%20Belgian%20cityscape%2C%20magical%20evening%20atmosphere&width=500&height=333&seq=belgium-dest-full-002&orientation=landscape',
      description: 'Descubra o charme europeu e chocolates',
      highlight: 'Bruxelas, Bruges, cerveja belga e waffles',
      alt: 'Grand Place em Bruxelas Bélgica com casas douradas iluminadas - turismo na Bélgica'
    },
    { 
      name: 'Luxemburgo', 
      flag: '🇱🇺', 
      image: 'https://readdy.ai/api/search-image?query=Luxembourg%20old%20town%20with%20medieval%20castle%20perched%20on%20cliff%20above%20river%20and%20stone%20bridge%2C%20picturesque%20European%20city%2C%20fortress%20walls%2C%20lush%20green%20valleys%2C%20professional%20travel%20photography%2C%20charming%20fairytale%20destination%2C%20stunning%20landscape&width=500&height=333&seq=luxembourg-dest-full-002&orientation=landscape',
      description: 'Explore o pequeno país com grande história',
      highlight: 'Castelos medievais, natureza e cultura',
      alt: 'Cidade velha de Luxemburgo com castelo medieval em penhasco - viagens para Luxemburgo'
    },
    { 
      name: 'Alemanha', 
      flag: '🇩🇪', 
      image: 'https://readdy.ai/api/search-image?query=Neuschwanstein%20Castle%20Germany%20nestled%20in%20Bavarian%20Alps%20mountains%20with%20snow%20peaks%2C%20fairytale%20white%20castle%2C%20dramatic%20mountain%20backdrop%2C%20stunning%20architecture%2C%20professional%20travel%20photography%2C%20iconic%20German%20landmark%2C%20majestic%20scenery%2C%20blue%20sky&width=500&height=333&seq=germany-dest-full-002&orientation=landscape',
      description: 'Visite castelos de conto de fadas',
      highlight: 'Berlim, Munique, castelos e Oktoberfest',
      alt: 'Castelo Neuschwanstein na Alemanha nos Alpes Bávaros - turismo na Alemanha'
    },
    { 
      name: 'Áustria', 
      flag: '🇦🇹', 
      image: 'https://readdy.ai/api/search-image?query=Vienna%20Austria%20Schonbrunn%20Palace%20with%20elegant%20baroque%20architecture%20and%20manicured%20gardens%2C%20imperial%20yellow%20building%2C%20classical%20European%20style%2C%20professional%20travel%20photography%2C%20cultural%20destination%2C%20grand%20historical%20landmark%2C%20beautiful%20sunny%20day&width=500&height=333&seq=austria-dest-full-002&orientation=landscape',
      description: 'Conheça a capital da música clássica',
      highlight: 'Viena, Salzburgo, Alpes e cultura imperial',
      alt: 'Palácio Schönbrunn em Viena Áustria com jardins imperiais - viagens culturais para Áustria'
    },
    { 
      name: 'Canadá', 
      flag: '🇨🇦', 
      image: 'https://readdy.ai/api/search-image?query=Canadian%20Rocky%20Mountains%20with%20pristine%20turquoise%20lake%20surrounded%20by%20pine%20forest%20and%20snow-capped%20peaks%2C%20stunning%20natural%20landscape%2C%20crystal%20clear%20water%2C%20professional%20travel%20photography%2C%20breathtaking%20North%20American%20wilderness%2C%20serene%20beauty&width=500&height=333&seq=canada-dest-full-002&orientation=landscape',
      description: 'Explore a natureza selvagem e cidades modernas',
      highlight: 'Toronto, Vancouver, Montanhas Rochosas',
      alt: 'Montanhas Rochosas Canadá com lago turquesa e picos nevados - natureza selvagem do Canadá'
    },
    { 
      name: 'Estados Unidos', 
      flag: '🇺🇸', 
      image: 'https://readdy.ai/api/search-image?query=New%20York%20City%20Manhattan%20skyline%20at%20golden%20hour%20with%20Empire%20State%20Building%20and%20modern%20skyscrapers%2C%20iconic%20American%20cityscape%2C%20vibrant%20urban%20architecture%2C%20warm%20sunset%20light%2C%20professional%20travel%20photography%2C%20breathtaking%20metropolitan%20view%2C%20clear%20blue%20sky&width=500&height=333&seq=usa-dest-full-003&orientation=landscape',
      description: 'Realize o sonho americano',
      highlight: 'Nova York, Miami, Las Vegas e parques',
      alt: 'Skyline de Manhattan Nova York com Empire State Building - viagens para Estados Unidos'
    },
    { 
      name: 'Suíça', 
      flag: '🇨🇭', 
      image: 'https://readdy.ai/api/search-image?query=Swiss%20Alps%20with%20majestic%20snow-capped%20mountain%20peaks%2C%20emerald%20green%20valleys%2C%20alpine%20meadows%20with%20wildflowers%2C%20stunning%20natural%20landscape%2C%20professional%20travel%20photography%2C%20breathtaking%20European%20scenery%2C%20pristine%20nature%2C%20clear%20blue%20sky&width=500&height=333&seq=switzerland-dest-full-002&orientation=landscape',
      description: 'Descubra os Alpes e a perfeição suíça',
      highlight: 'Zurique, Genebra, montanhas e chocolate',
      alt: 'Alpes Suíços com picos nevados e vales verdes - turismo de montanha na Suíça'
    },
    { 
      name: 'Reino Unido', 
      flag: '🇬🇧', 
      image: 'https://readdy.ai/api/search-image?query=London%20Big%20Ben%20clock%20tower%20and%20Parliament%20buildings%20beside%20Thames%20river%20at%20sunset%2C%20iconic%20British%20landmark%2C%20gothic%20architecture%2C%20warm%20golden%20light%2C%20professional%20travel%20photography%2C%20classic%20UK%20destination%2C%20majestic%20cityscape&width=500&height=333&seq=uk-dest-full-002&orientation=landscape',
      description: 'Visite a realeza e história britânica',
      highlight: 'Londres, Escócia, castelos e cultura',
      alt: 'Big Ben e Parlamento em Londres Reino Unido ao pôr do sol - viagens para Inglaterra'
    },
  ];

  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setGalleryOpen(true);
  };

  const closeGallery = () => {
    setGalleryOpen(false);
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % destinations.length);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + destinations.length) % destinations.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navegação */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/821bfe14b1fc609bf2a57dd541a10a42.png" 
                alt="FF TRAVEL - Agência de Viagens" 
                className="h-32 md:h-36"
                loading="eager"
              />
            </Link>
            
            <div className="hidden md:flex items-center space-x-8 animate-slide-down">
              <Link to="/" className={`${isScrolled ? 'text-[#ff6b35]' : 'text-white'} font-semibold transition-all duration-300 hover:scale-110`}>
                Início
              </Link>
              <Link to="/agencia-viagens" className={`${isScrolled ? 'text-[#1e40af]' : 'text-white'} hover:text-[#ff6b35] transition-all duration-300 font-medium hover:scale-110`}>
                Agência de Viagens
              </Link>
              <Link to="/loja-servicos" className={`${isScrolled ? 'text-[#1e40af]' : 'text-white'} hover:text-[#ff6b35] transition-all duration-300 font-medium hover:scale-110`}>
                Loja & Serviços
              </Link>
              <Link to="/sobre" className={`${isScrolled ? 'text-[#1e40af]' : 'text-white'} hover:text-[#ff6b35] transition-all duration-300 font-medium hover:scale-110`}>
                Sobre
              </Link>
              <Link to="/contactos" className={`${isScrolled ? 'text-[#1e40af]' : 'text-white'} hover:text-[#ff6b35] transition-all duration-300 font-medium hover:scale-110`}>
                Contactos
              </Link>
            </div>

            <a 
              href="https://wa.me/244936769113" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:block bg-[#ff6b35] text-white px-6 py-3 rounded-full font-medium hover:bg-[#ff5722] transition-all duration-300 shadow-md whitespace-nowrap cursor-pointer hover-lift hover-glow animate-slide-in-right"
            >
              Pedir Cotação
            </a>
            
            <Link 
              to="/agendamento"
              className="hidden md:block bg-[#1e40af] text-white px-6 py-3 rounded-full font-medium hover:bg-[#1e3a8a] transition-all duration-300 shadow-md whitespace-nowrap cursor-pointer hover-lift animate-slide-in-right animation-delay-200"
            >
              Agendar Atendimento
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero com Carrossel Dinâmico */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Carrossel de Imagens - Otimizado */}
        <div className="absolute inset-0">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentHeroSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* Carregar apenas slide atual e próximo */}
              {(index === currentHeroSlide || index === (currentHeroSlide + 1) % heroSlides.length) && (
                <img 
                  src={slide.image}
                  alt={slide.destination}
                  className="w-full h-full object-cover object-center"
                  loading={index === 0 ? "eager" : "lazy"}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            </div>
          ))}
          
          {/* Elementos decorativos flutuantes */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-float"></div>
          <div className="absolute bottom-40 right-20 w-32 h-32 bg-[#ff6b35]/20 rounded-full blur-2xl animate-float animation-delay-500"></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-[#1e40af]/20 rounded-full blur-xl animate-pulse-slow"></div>
        </div>

        {/* Conteúdo do Hero */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
          {/* Badge do Destino Atual */}
          <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md border-2 border-white/50 px-8 py-4 rounded-full mb-6 animate-bounce-slow">
            <span className="text-4xl">{heroSlides[currentHeroSlide].flag}</span>
            <span className="text-xl font-bold">{heroSlides[currentHeroSlide].destination}</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-4 leading-tight animate-slide-up">
            {heroSlides[currentHeroSlide].tagline}
          </h1>
          <p className="text-2xl md:text-3xl mb-10 text-white/95 max-w-3xl mx-auto leading-relaxed animate-slide-up animation-delay-200 font-semibold">
            {heroSlides[currentHeroSlide].highlight}
          </p>
          <p className="text-lg md:text-xl mb-10 text-white/90 max-w-3xl mx-auto leading-relaxed animate-slide-up animation-delay-300">
            Passagens aéreas, pacotes turísticos, vistos e reservas com atendimento personalizado
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up animation-delay-400">
            <a 
              href="https://wa.me/244936769113" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#ff6b35] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer hover-lift hover-glow"
            >
              Pedir Cotação
            </a>
            <Link 
              to="/agendamento"
              className="bg-[#1e40af] text-white px-10 py-5 rounded-full font-medium hover:bg-[#1e3a8a] transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer hover-lift"
            >
              Agendar Atendimento
            </Link>
          </div>
        </div>

        {/* Controles do Carrossel */}
        <button
          onClick={prevHeroSlide}
          className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 w-14 h-14 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer hover-scale"
        >
          <i className="ri-arrow-left-s-line text-3xl"></i>
        </button>

        <button
          onClick={nextHeroSlide}
          className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 w-14 h-14 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer hover-scale"
        >
          <i className="ri-arrow-right-s-line text-3xl"></i>
        </button>

        {/* Indicadores de Slides */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-20 text-white text-center">
          <p className="text-sm font-semibold bg-black/30 backdrop-blur-sm px-6 py-2 rounded-full">
            {currentHeroSlide + 1} / {heroSlides.length} - Agência FF Travel
          </p>
        </div>
      </section>

      {/* Serviços Principais */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Elementos decorativos de fundo */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1e40af]/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#ff6b35]/5 rounded-full blur-3xl animate-pulse-slow animation-delay-500"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">
              Serviços de Viagem
            </h2>
            <p className="text-gray-600 text-lg">
              Soluções completas para suas viagens internacionais
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 scroll-reveal hover-lift">
              <div className="w-16 h-16 bg-[#1e40af]/10 rounded-2xl flex items-center justify-center mb-6 animate-bounce-slow">
                <i className="ri-plane-line text-4xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Passagens Aéreas</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Compra de passagens aéreas e reservas para todos os destinos
              </p>
              <Link to="/agencia-viagens" className="text-[#ff6b35] font-semibold hover:underline cursor-pointer transition-all duration-300 hover:scale-105 inline-block">
                Saber mais →
              </Link>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 scroll-reveal hover-lift animation-delay-200">
              <div className="w-16 h-16 bg-[#1e40af]/10 rounded-2xl flex items-center justify-center mb-6 animate-bounce-slow animation-delay-200">
                <i className="ri-passport-line text-4xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Vistos</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Processo de obtenção de vistos em poucos dias
              </p>
              <Link to="/agencia-viagens" className="text-[#ff6b35] font-semibold hover:underline cursor-pointer transition-all duration-300 hover:scale-105 inline-block">
                Saber mais →
              </Link>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 scroll-reveal hover-lift animation-delay-400">
              <div className="w-16 h-16 bg-[#1e40af]/10 rounded-2xl flex items-center justify-center mb-6 animate-bounce-slow animation-delay-400">
                <i className="ri-suitcase-line text-4xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Pacotes Turísticos</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Pacotes completos para viagens internacionais
              </p>
              <Link to="/agencia-viagens" className="text-[#ff6b35] font-semibold hover:underline cursor-pointer transition-all duration-300 hover:scale-105 inline-block">
                Saber mais →
              </Link>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 scroll-reveal hover-lift animation-delay-600">
              <div className="w-16 h-16 bg-[#1e40af]/10 rounded-2xl flex items-center justify-center mb-6 animate-bounce-slow animation-delay-600">
                <i className="ri-hotel-line text-4xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Hotéis & Seguro</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Reservas de hotéis e seguro de viagens
              </p>
              <Link to="/agencia-viagens" className="text-[#ff6b35] font-semibold hover:underline cursor-pointer transition-all duration-300 hover:scale-105 inline-block">
                Saber mais →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Destinos Disponíveis */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">
              Destinos Disponíveis
            </h2>
            <p className="text-gray-600 text-lg">
              Viaje para os principais destinos internacionais com a FFTravel
            </p>
          </div>

          {/* Vídeo em Destaque */}
          <div className="mb-16 scroll-reveal">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] p-1">
              <div className="bg-white rounded-3xl overflow-hidden">
                <div className="relative aspect-video">
                  <iframe
                    src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/ec646304adf8ddc925c6467ad8ff60ac.jpeg"
                    className="w-full h-full object-cover"
                    title="Destinos FF Travel"
                    loading="lazy"
                    allowFullScreen
                  ></iframe>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="max-w-3xl">
                      <div className="inline-block bg-[#ff6b35] px-4 py-2 rounded-full text-sm font-bold mb-4 animate-bounce-slow">
                        ✈️ Explore o Mundo
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold mb-3 drop-shadow-2xl">
                        Descubra Destinos Incríveis
                      </h3>
                      <p className="text-lg text-white/90 mb-6 drop-shadow-lg">
                        Viaje com segurança e conforto para os melhores destinos internacionais
                      </p>
                      <a 
                        href="https://wa.me/244936769113" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 bg-[#ff6b35] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer hover-lift hover-glow"
                      >
                        <i className="ri-whatsapp-line text-2xl"></i>
                        Solicitar Cotação
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {destinations.map((dest, index) => (
              <div 
                key={index} 
                className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl group cursor-pointer scroll-reveal hover-lift"
                onClick={() => openGallery(index)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Imagem de Fundo - Lazy Loading */}
                <img 
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                
                {/* Overlay Gradiente */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
                
                {/* Conteúdo */}
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-2xl px-12">
                    {/* Bandeira */}
                    <div className="text-7xl mb-6 drop-shadow-2xl animate-bounce-slow">{dest.flag}</div>
                    
                    {/* Nome do Destino */}
                    <h3 className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-2xl transform group-hover:scale-105 transition-transform duration-500">
                      {dest.name}
                    </h3>
                    
                    {/* Descrição */}
                    <p className="text-2xl text-white/95 mb-3 drop-shadow-lg font-medium transform group-hover:translate-x-2 transition-transform duration-500">
                      {dest.description}
                    </p>
                    
                    {/* Destaques */}
                    <p className="text-lg text-white/80 mb-8 drop-shadow-lg transform group-hover:translate-x-2 transition-transform duration-500">
                      {dest.highlight}
                    </p>
                    
                    {/* Botões de Ação */}
                    <div className="flex gap-4 transform group-hover:translate-x-2 transition-transform duration-500">
                      <a 
                        href="https://wa.me/244936769113" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#ff6b35] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3 hover-glow"
                      >
                        <i className="ri-whatsapp-line text-2xl"></i>
                        Solicitar Cotação
                      </a>
                      <button
                        className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all duration-300 shadow-2xl whitespace-nowrap inline-flex items-center gap-3 border-2 border-white/50"
                      >
                        <i className="ri-eye-line text-2xl"></i>
                        Ver Detalhes
                      </button>
                    </div>
                  </div>
                </div>

                {/* Ícone de Zoom */}
                <div className="absolute top-6 right-6 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 animate-pulse-slow">
                  <i className="ri-zoom-in-line text-white text-3xl"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16 scroll-reveal">
            <p className="text-gray-600 text-lg mb-6">
              Não encontrou o destino que procura? Entre em contacto connosco!
            </p>
            <a 
              href="https://wa.me/244936769113" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-[#ff6b35] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer hover-lift hover-glow"
            >
              Falar com Especialista
            </a>
          </div>
        </div>
      </section>

      {/* Galeria em Tela Cheia */}
      {galleryOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in">
          {/* Botão Fechar */}
          <button
            onClick={closeGallery}
            className="absolute top-6 right-6 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all duration-300 z-10 cursor-pointer hover-scale"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>

          {/* Contador */}
          <div className="absolute top-6 left-6 text-white text-lg font-semibold z-10 animate-slide-in-left">
            {currentImageIndex + 1} / {destinations.length}
          </div>

          {/* Botão Anterior */}
          <button
            onClick={goToPrevious}
            className="absolute left-6 w-16 h-16 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer hover-scale"
          >
            <i className="ri-arrow-left-s-line text-4xl"></i>
          </button>

          {/* Imagem Principal */}
          <div className="max-w-6xl max-h-[90vh] mx-auto px-24 animate-scale-in">
            <img
              src={destinations[currentImageIndex].image}
              alt={destinations[currentImageIndex].name}
              className="w-full h-full object-contain rounded-2xl"
              loading="eager"
            />
            <div className="text-center mt-6">
              <div className="text-6xl mb-4 animate-bounce-slow">{destinations[currentImageIndex].flag}</div>
              <h3 className="text-white text-4xl font-bold mb-3">{destinations[currentImageIndex].name}</h3>
              <p className="text-white/90 text-xl mb-2">{destinations[currentImageIndex].description}</p>
              <p className="text-white/70 text-lg mb-6">{destinations[currentImageIndex].highlight}</p>
              <a 
                href="https://wa.me/244936769113" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-[#ff6b35] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer hover-lift hover-glow"
              >
                Solicitar Cotação
              </a>
            </div>
          </div>

          {/* Botão Próximo */}
          <button
            onClick={goToNext}
            className="absolute right-6 w-16 h-16 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer hover-scale"
          >
            <i className="ri-arrow-right-s-line text-4xl"></i>
          </button>

          {/* Miniaturas */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3 overflow-x-auto max-w-[90vw] pb-2">
            {destinations.map((dest, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-3 transition-all duration-300 cursor-pointer ${
                  index === currentImageIndex ? 'border-[#ff6b35] scale-110' : 'border-white/30 opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Testemunhos em Vídeo */}
      <section className="py-24 bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] relative overflow-hidden">
        {/* Elementos decorativos animados */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#ff6b35]/10 rounded-full blur-3xl animate-float animation-delay-500"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16 scroll-reveal">
            <div className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-bounce-slow">
              Testemunhos Reais
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Clientes que Chegaram Longe
            </h2>
            <p className="text-white/90 text-lg max-w-3xl mx-auto">
              Veja os testemunhos de clientes que realizaram seus sonhos de viajar através da FFTravel
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Vídeo 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-500 group scroll-reveal hover-lift">
              <div className="relative aspect-[9/16] overflow-hidden">
                <iframe
                  src="https://www.instagram.com/reel/DFvvsXvsVVe/embed"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  loading="lazy"
                  allowFullScreen
                  title="Testemunho Cliente 1"
                ></iframe>
              </div>
              <div className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 text-white">
                  <i className="ri-user-smile-line text-2xl animate-bounce-slow"></i>
                  <span className="font-semibold">Cliente Satisfeito</span>
                </div>
              </div>
            </div>

            {/* Vídeo 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-500 group scroll-reveal hover-lift animation-delay-200">
              <div className="relative aspect-[9/16] overflow-hidden">
                <iframe
                  src="https://www.instagram.com/reel/DR5G_O0jsZL/embed"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  loading="lazy"
                  allowFullScreen
                  title="Testemunho Cliente 2"
                ></iframe>
              </div>
              <div className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 text-white">
                  <i className="ri-user-smile-line text-2xl animate-bounce-slow animation-delay-200"></i>
                  <span className="font-semibold">Cliente Satisfeito</span>
                </div>
              </div>
            </div>

            {/* Vídeo 3 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-500 group scroll-reveal hover-lift animation-delay-400">
              <div className="relative aspect-[9/16] overflow-hidden">
                <iframe
                  src="https://www.instagram.com/reel/DRUJ0UEEbFa/embed"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  loading="lazy"
                  allowFullScreen
                  title="Testemunho Cliente 3"
                ></iframe>
              </div>
              <div className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 text-white">
                  <i className="ri-user-smile-line text-2xl animate-bounce-slow animation-delay-400"></i>
                  <span className="font-semibold">Cliente Satisfeito</span>
                </div>
              </div>
            </div>

            {/* Vídeo 4 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-500 group scroll-reveal hover-lift animation-delay-600">
              <div className="relative aspect-[9/16] overflow-hidden">
                <iframe
                  src="https://www.instagram.com/reel/DSclhrGjq3P/embed"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  loading="lazy"
                  allowFullScreen
                  title="Testemunho Cliente 4"
                ></iframe>
              </div>
              <div className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 text-white">
                  <i className="ri-user-smile-line text-2xl animate-bounce-slow animation-delay-600"></i>
                  <span className="font-semibold">Cliente Satisfeito</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 scroll-reveal">
            <p className="text-white/80 text-lg mb-6">
              Quer ser o próximo a realizar seu sonho de viajar?
            </p>
            <a 
              href="https://wa.me/244936769113" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-[#ff6b35] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer hover-lift hover-glow"
            >
              Começar Minha Viagem
            </a>
          </div>
        </div>
      </section>

      {/* Atendimento Profissional */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#ff6b35]/5 rounded-full blur-3xl animate-pulse-slow"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="scroll-reveal">
              <div className="inline-block bg-[#ff6b35]/10 text-[#ff6b35] px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-bounce-slow">
                Atendimento Profissional
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-6">
                Equipa Dedicada ao Seu Serviço
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Na FFTravel, contamos com uma equipa profissional e experiente, pronta para oferecer o melhor atendimento e garantir que sua viagem seja perfeita do início ao fim.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <i className="ri-check-line text-2xl text-[#ff6b35] flex-shrink-0 animate-bounce-slow"></i>
                  <span className="text-gray-700">Atendimento personalizado e dedicado</span>
                </li>
                <li className="flex items-start gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <i className="ri-check-line text-2xl text-[#ff6b35] flex-shrink-0 animate-bounce-slow animation-delay-200"></i>
                  <span className="text-gray-700">Equipa experiente em turismo internacional</span>
                </li>
                <li className="flex items-start gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <i className="ri-check-line text-2xl text-[#ff6b35] flex-shrink-0 animate-bounce-slow animation-delay-400"></i>
                  <span className="text-gray-700">Suporte completo em todas as etapas</span>
                </li>
                <li className="flex items-start gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <i className="ri-check-line text-2xl text-[#ff6b35] flex-shrink-0 animate-bounce-slow animation-delay-600"></i>
                  <span className="text-gray-700">Compromisso com a satisfação do cliente</span>
                </li>
              </ul>
              <a 
                href="https://wa.me/244936769113" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-[#ff6b35] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#ff5722] transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer hover-lift hover-glow"
              >
                Falar com a Equipa
              </a>
            </div>
            <div className="relative scroll-reveal animation-delay-300">
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#ff6b35]/20 rounded-full blur-2xl animate-pulse-slow"></div>
              <img 
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/ba304f76653834f3553a8193a3f29f4f.jpeg"
                alt="Equipa FF Travel"
                className="rounded-3xl shadow-2xl w-full hover-scale"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">
              Por que escolher a FFTravel?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center scroll-reveal hover-lift">
              <div className="w-20 h-20 bg-[#1e40af]/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-slow">
                <i className="ri-time-line text-5xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Rapidez</h3>
              <p className="text-gray-600 leading-relaxed">
                Processo de obtenção de vistos em poucos dias e atendimento ágil
              </p>
            </div>

            <div className="text-center scroll-reveal hover-lift animation-delay-200">
              <div className="w-20 h-20 bg-[#1e40af]/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-slow animation-delay-200">
                <i className="ri-shield-check-line text-5xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Confiança</h3>
              <p className="text-gray-600 leading-relaxed">
                Empresa registada com NIF 500714988 e atendimento profissional
              </p>
            </div>

            <div className="text-center scroll-reveal hover-lift animation-delay-400">
              <div className="w-20 h-20 bg-[#1e40af]/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-slow animation-delay-400">
                <i className="ri-customer-service-2-line text-5xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">Suporte</h3>
              <p className="text-gray-600 leading-relaxed">
                Atendimento personalizado e suporte completo durante toda a viagem
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Loja & Serviços */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-4">
              Loja & Serviços
            </h2>
            <p className="text-gray-600">
              Serviços complementares disponíveis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-500 scroll-reveal hover-lift">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4 animate-bounce-slow">
                <i className="ri-smartphone-line text-3xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Venda de Telemóveis</h3>
              <p className="text-gray-600 mb-4">
                Vendemos telemóveis e acessórios
              </p>
              <Link to="/loja-servicos" className="text-[#ff6b35] font-semibold hover:underline cursor-pointer text-sm transition-all duration-300 hover:scale-105 inline-block">
                Ver mais →
              </Link>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-500 scroll-reveal hover-lift animation-delay-200">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4 animate-bounce-slow animation-delay-200">
                <i className="ri-vip-diamond-line text-3xl text-[#1e40af]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Compra de Ouro</h3>
              <p className="text-gray-600 mb-4">
                Compramos todo tipo de ouro: alianças, anéis, fios, pulseiras
              </p>
              <Link to="/loja-servicos" className="text-[#ff6b35] font-semibold hover:underline cursor-pointer text-sm transition-all duration-300 hover:scale-105 inline-block">
                Ver mais →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full blur-3xl animate-float animation-delay-500"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 scroll-reveal">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Pronto para Viajar?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Entre em contacto e receba uma cotação personalizada
          </p>
          <a 
            href="https://wa.me/244936769113" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block bg-[#ff6b35] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-[#ff5722] transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer hover-lift hover-glow"
          >
            Falar no WhatsApp
          </a>
        </div>
      </section>

      {/* Rodapé */}
      <footer className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/3c15764482320d2816a8cc3fe11ecba0/e349152b587c7809cba7381685cd68b4.png" 
                alt="FF TRAVEL - Agência de Viagens" 
                className="h-24 md:h-28 mb-8"
                loading="lazy"
              />
              <p className="text-white/70 leading-relaxed mb-4">
                FF Prestação & Serviço, Lda
              </p>
              <p className="text-white/50 text-sm mb-6">
                NIF: 500714988
              </p>
              
              {/* Redes Sociais */}
              <div className="mb-6">
                <p className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">
                  Siga-nos
                </p>
                <div className="flex gap-3">
                  <a 
                    href="https://www.instagram.com/ff_travel_angola/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:scale-110 rounded-xl flex items-center justify-center transition-all shadow-lg cursor-pointer"
                    title="Instagram"
                  >
                    <i className="ri-instagram-line text-2xl text-white"></i>
                  </a>
                  <a 
                    href="https://www.facebook.com/share/1AVqwyfH7E/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1877F2] hover:scale-110 rounded-xl flex items-center justify-center transition-all shadow-lg cursor-pointer"
                    title="Facebook"
                  >
                    <i className="ri-facebook-fill text-2xl text-white"></i>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/ff-travel" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#0A66C2] hover:scale-110 rounded-xl flex items-center justify-center transition-all shadow-lg cursor-pointer"
                    title="LinkedIn"
                  >
                    <i className="ri-linkedin-fill text-2xl text-white"></i>
                  </a>
                </div>
              </div>
            </div>

            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Contactos</p>
              <div className="space-y-3">
                <a href="tel:+244936769113" className="block text-white hover:text-[#ff6b35] transition-colors cursor-pointer">
                  +244 936 769 113
                </a>
                <a href="tel:+244927606910" className="block text-white hover:text-[#ff6b35] transition-colors cursor-pointer">
                  +244 927 606 910
                </a>
              </div>
            </div>

            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Localização</p>
              <p className="text-white/70 leading-relaxed">
                Rua do Campo do Escoqueiro<br />
                De frente ao prédio do Cucu<br />
                Luanda, Angola
              </p>
            </div>

            <div>
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Links Rápidos</p>
              <div className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#ff6b35] transition-colors">Início</Link>
                <Link to="/agencia-viagens" className="block text-white hover:text-[#ff6b35] transition-colors">Agência de Viagens</Link>
                <Link to="/loja-servicos" className="block text-white hover:text-[#ff6b35] transition-colors">Loja & Serviços</Link>
                <Link to="/sobre" className="block text-white hover:text-[#ff6b35] transition-colors">Sobre</Link>
                <Link to="/contactos" className="block text-white hover:text-[#ff6b35] transition-colors">Contactos</Link>
                <Link to="/politicas-privacidade" className="block text-white hover:text-[#ff6b35] transition-colors">Políticas de Privacidade</Link>
                <Link to="/termos-condicoes" className="block text-white hover:text-[#ff6b35] transition-colors">Termos e Condições</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/50 text-sm">
                © 2025 FF TRAVEL. Todos os direitos reservados.
              </p>
              <div className="flex items-center gap-6">
                <Link to="/termos-condicoes" className="text-white/50 hover:text-white transition-colors text-sm">
                  Termos e Condições
                </Link>
                <Link to="/politicas-privacidade" className="text-white/50 hover:text-white transition-colors text-sm">
                  Políticas de Privacidade
                </Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors cursor-pointer text-sm">Web Design</a>
              </div>
            </div>
            
            {/* Desenvolvido por Snurf */}
            <div className="mt-6 text-center">
              <p className="text-white/40 text-xs">
                Desenvolvido por <span className="text-white/60 font-semibold">Snurf</span>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Botão WhatsApp Flutuante - ÚNICO */}
      <a 
        href="https://wa.me/244936769113" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-8 left-8 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-transform cursor-pointer animate-pulse"
        style={{ 
          zIndex: 9999,
          boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)' 
        }}
        aria-label="Contactar via WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl"></i>
      </a>

      {/* Estilos CSS personalizados */}
      <style jsx>{`
        .animate-slide-in-left {
          animation: slideInLeft 0.6s ease-out forwards;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.6s ease-out forwards;
        }
        
        .animate-slide-up {
          animation: slideUp 0.8s ease-out forwards;
        }
        
        .animate-slide-down {
          animation: slideDown 0.8s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulseSlow 4s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounceSlow 6s ease-in-out infinite;
        }
        
        .animate-scale-in {
          animation: scaleIn 0.5s ease-out forwards;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out forwards;
        }
        
        .hover-scale {
          transition: transform 0.3s ease;
        }
        
        .hover-scale:hover {
          transform: scale(1.05);
        }
        
        .hover-lift {
          transition: transform 0.3s ease;
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
        }
        
        .hover-glow {
          box-shadow: 0 0 20px rgba(255, 107, 53, 0.3);
        }
        
        .hover-glow:hover {
          box-shadow: 0 0 30px rgba(255, 107, 53, 0.4);
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes pulseSlow {
          0%, 100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }
        
        @keyframes bounceSlow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(1.1);
          }
          to {
            transform: scale(1);
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        
        .scroll-reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease-out;
        }
        
        .scroll-reveal.revealed {
          opacity: 1;
          transform: translateY(0);
        }
        
        .hover-lift {
          transition: transform 0.3s ease;
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
        }
        
        .hover-glow {
          box-shadow: 0 0 20px rgba(255, 107, 53, 0.3);
        }
        
        .hover-glow:hover {
          box-shadow: 0 0 30px rgba(255, 107, 53, 0.4);
        }
      `}</style>
    </div>
  );
}
